import { ComponentProps, ReactNode } from 'react';
import clsx from 'clsx';

import styles from './PaginatedItemsGrid.module.css';

interface PaginatedItemsGridProps extends Omit<ComponentProps<'section'>, 'children'> {
  items: ReactNode[];
  currentPage: number;
  numberOfPages: number;
  onNextClick?: () => void;
  onPreviousClick?: () => void;
  onPageClick?: (page: number) => void;
  heading?: ReactNode;
  supportingText?: ReactNode;
  previousLabel?: ReactNode;
  nextLabel?: ReactNode;
  loadMoreLabel?: ReactNode;
  itemHeight?: string;
}

export default function PaginatedItemsGrid({
  items,
  currentPage,
  numberOfPages,
  onNextClick,
  onPreviousClick,
  onPageClick,
  heading,
  supportingText,
  previousLabel,
  nextLabel,
  loadMoreLabel,
  className,
  itemHeight,
  style,
  ...props
}: PaginatedItemsGridProps) {
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= numberOfPages;
  const pages = Array.from({ length: numberOfPages }, (_, index) => index + 1);

  return (
    <section
      className={clsx(styles.section, className)}
      style={{ '--h': itemHeight, style } as object}
      {...props}
    >
      <div className={styles.container}>
        {heading || supportingText ? (
          <div className={styles.header}>
            {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
            {supportingText ? <p className={styles.supportingText}>{supportingText}</p> : null}
          </div>
        ) : null}

        <div className={styles.itemsGrid}>
          {items.map((item, index) => (
            <div
              key={index}
              className={styles.cell}
            >
              {item}
            </div>
          ))}
        </div>

        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.navButton}
            onClick={onPreviousClick}
            disabled={isFirstPage}
          >
            {previousLabel}
          </button>

          <div className={styles.pageNumbers}>
            {pages.map((page) => (
              <button
                key={page}
                type="button"
                className={clsx(styles.pageNumber, page === currentPage && styles.pageNumberActive)}
                onClick={() => onPageClick?.(page)}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            type="button"
            className={styles.navButton}
            onClick={onNextClick}
            disabled={isLastPage}
          >
            {nextLabel}
          </button>
        </div>

        {!isLastPage ? (
          <div className={styles.loadMore}>
            <button
              type="button"
              className={styles.navButton}
              onClick={onNextClick}
            >
              {loadMoreLabel}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
